import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatRON } from "../lib/shop";
import { useT } from "../lib/i18n";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronLeft, ShoppingBag, Package, Languages, Truck, Mail, Download, RefreshCw, CheckCircle, AlertTriangle, Undo2 } from "lucide-react";
import ProductForm from "../components/ProductForm";

const STATUSES = ["nou", "procesat", "expediat", "livrat", "anulat", "retur acceptat"];

export default function Admin() {
  const { user, loading } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/login");
  }, [user, loading, nav]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="admin-page">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-gray-400">{t("admin.eye")}</p>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">ANAIZA SHOP · ADMIN</h1>

      <div className="mt-10 border-b border-gray-200 flex gap-1" data-testid="admin-tabs">
        <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} Icon={ShoppingBag} testid="tab-orders">{t("admin.tab.orders")}</TabBtn>
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} Icon={Package} testid="tab-products">{t("admin.tab.products")}</TabBtn>
      </div>

      <div className="mt-8">
        {tab === "orders" ? <OrdersTab /> : <ProductsTab />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] border-b-2 -mb-px transition ${active ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
    >
      <Icon size={14} strokeWidth={1.5} /> {children}
    </button>
  );
}

/* ---------------------- ORDERS ---------------------- */
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [sdStatus, setSdStatus] = useState(null);
  const [busy, setBusy] = useState({});

  const load = () => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  const loadSamedayStatus = () => api.get("/admin/sameday/status").then((r) => setSdStatus(r.data)).catch(() => {});
  useEffect(() => { load(); loadSamedayStatus(); }, []);

  const changeStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status });
      toast.success("Status actualizat");
      load();
    } catch { toast.error("Eroare la actualizare"); }
  };

  const generateAwb = async (oid) => {
    setBusy((b) => ({ ...b, [oid]: "awb" }));
    try {
      await api.post(`/admin/orders/${oid}/awb`);
      toast.success("AWB generat și email trimis");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la generarea AWB");
    } finally { setBusy((b) => ({ ...b, [oid]: null })); }
  };

  const resendEmail = async (oid) => {
    setBusy((b) => ({ ...b, [oid]: "email" }));
    try {
      await api.post(`/admin/orders/${oid}/resend-email`);
      toast.success("Email retrimis");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la retrimitere email");
    } finally { setBusy((b) => ({ ...b, [oid]: null })); }
  };

  const acceptReturn = async (oid) => {
    if (!window.confirm("Confirmi că accepți returul pentru această comandă? Clientul va primi email cu confirmarea.")) return;
    setBusy((b) => ({ ...b, [oid]: "return" }));
    try {
      await api.post(`/admin/orders/${oid}/return-accepted`);
      toast.success("Retur acceptat · email trimis clientului");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la procesarea returului");
    } finally { setBusy((b) => ({ ...b, [oid]: null })); }
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Comenzi totale", v: stats.total_orders },
            { l: "Comenzi plătite", v: stats.paid_orders },
            { l: "Venit total", v: formatRON(stats.revenue) },
            { l: "Utilizatori", v: stats.users },
          ].map((s, i) => (
            <div key={i} className="border border-gray-200 p-5 bg-gray-50/50">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{s.l}</p>
              <p className="font-display text-3xl font-black font-mono mt-3">{s.v}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 flex items-center gap-2 flex-wrap">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 text-xs uppercase tracking-wider border ${filter === s ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-600 hover:border-gray-900'}`} data-testid={`filter-${s}`}>
            {s === "all" ? "Toate" : s}
          </button>
        ))}
      </div>

      {sdStatus && (
        <div className={`mt-4 border px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${sdStatus.configured ? 'border-green-500 bg-green-50' : 'border-amber-500 bg-amber-50'}`} data-testid="sameday-status-banner">
          <div className="flex items-start gap-2 text-sm">
            {sdStatus.configured ? <CheckCircle size={18} strokeWidth={1.5} className="text-green-700 mt-0.5" /> : <AlertTriangle size={18} strokeWidth={1.5} className="text-amber-700 mt-0.5" />}
            <div>
              <p className={`font-bold uppercase tracking-wider text-[10px] ${sdStatus.configured ? 'text-green-800' : 'text-amber-800'}`}>Integrare Sameday</p>
              {sdStatus.configured ? (
                <p className="text-xs text-green-800 mt-1">Activă · pickup point #{sdStatus.pickup_point_id} · serviciu #{sdStatus.service_id} · AWB se generează automat la fiecare comandă nouă (COD) sau plată confirmată.</p>
              ) : (
                <p className="text-xs text-amber-800 mt-1">Inactivă: {sdStatus.error}. AWB-urile se vor putea genera de îndată ce credentialele Sameday vor fi corecte. Emailul cu detaliile comenzii se trimite oricum.</p>
              )}
            </div>
          </div>
          <button onClick={loadSamedayStatus} className="text-xs uppercase tracking-wider border border-gray-300 px-3 py-1.5 hover:border-gray-900 inline-flex items-center gap-1" data-testid="refresh-sameday-status">
            <RefreshCw size={12} /> Reîncearcă
          </button>
        </div>
      )}

      <div className="mt-6 border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">Comandă</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Plată</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">AWB</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((o) => (
              <React.Fragment key={o.id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(expanded === o.id ? null : o.id)} data-testid={`admin-order-${o.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold">#{o.id.slice(0, 8).toUpperCase()}</td>
                  <td className="px-4 py-3 text-gray-600">{new Date(o.created_at).toLocaleString("ro-RO")}</td>
                  <td className="px-4 py-3">{o.shipping.full_name} <br/><span className="text-xs text-gray-500">{o.shipping.phone}</span></td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-mono px-2 py-1 border ${o.payment_status === 'paid' ? 'border-green-500 text-green-700' : 'border-gray-300 text-gray-600'}`}>
                      {o.payment_method.toUpperCase()} · {o.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{formatRON(o.total)}</td>
                  <td className="px-4 py-3">
                    <select value={o.status} onChange={(e) => { e.stopPropagation(); changeStatus(o.id, e.target.value); }} onClick={(e) => e.stopPropagation()}
                      className="border border-gray-300 px-2 py-1 text-xs uppercase tracking-wider bg-white" data-testid={`status-select-${o.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      {o.awb_number ? (
                        <>
                          <span className="font-mono text-[11px] font-bold text-green-700 border border-green-500 px-1.5 py-0.5" data-testid={`awb-num-${o.id}`}>{o.awb_number}</span>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/awb/${o.awb_number}.pdf?_t=${localStorage.getItem('anaiza_token')}`}
                             target="_blank" rel="noreferrer"
                             onClick={(e) => { e.preventDefault(); api.get(`/admin/awb/${o.awb_number}.pdf`, { responseType: 'blob' }).then((r) => { const u = URL.createObjectURL(r.data); window.open(u, '_blank'); }); }}
                             className="p-1.5 border border-gray-300 hover:border-gray-900" title="Descarcă PDF" data-testid={`awb-download-${o.id}`}>
                            <Download size={12} />
                          </a>
                          <button onClick={() => resendEmail(o.id)} disabled={busy[o.id]} className="p-1.5 border border-gray-300 hover:border-gray-900 disabled:opacity-50" title="Retrimite email" data-testid={`email-resend-${o.id}`}>
                            <Mail size={12} className={busy[o.id] === "email" ? "animate-pulse" : ""} />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => generateAwb(o.id)} disabled={busy[o.id]} className="inline-flex items-center gap-1 px-2 py-1 border border-gray-900 text-[11px] font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white transition disabled:opacity-50" data-testid={`generate-awb-${o.id}`}>
                          <Truck size={11} className={busy[o.id] === "awb" ? "animate-pulse" : ""} /> {busy[o.id] === "awb" ? "..." : "Generează"}
                        </button>
                      )}
                      {o.status === "retur acceptat" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 border border-amber-500 bg-amber-50 px-1.5 py-1" title={`Acceptat la ${o.return_accepted_at}`}>
                          <Undo2 size={11} /> Retur OK
                        </span>
                      ) : (
                        <button onClick={() => acceptReturn(o.id)} disabled={busy[o.id]} className="p-1.5 border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white transition disabled:opacity-50" title="Acceptă retur (trimite email clientului)" data-testid={`accept-return-${o.id}`}>
                          <Undo2 size={12} className={busy[o.id] === "return" ? "animate-pulse" : ""} />
                        </button>
                      )}
                    </div>
                    {o.awb_error && !o.awb_number && (
                      <p className="text-[10px] text-red-600 mt-1 max-w-[200px] line-clamp-2" title={o.awb_error}>{o.awb_error}</p>
                    )}
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={7} className="px-4 py-5">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Articole</p>
                          <ul className="space-y-2 text-sm">
                            {o.items.map((i, idx) => (
                              <li key={idx} className="flex justify-between">
                                <span>{i.name} <span className="text-gray-500">× {i.quantity}</span></span>
                                <span className="font-mono">{formatRON(i.line_total)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Livrare</p>
                          <p className="text-sm">{o.shipping.full_name}</p>
                          <p className="text-sm">{o.shipping.address}</p>
                          <p className="text-sm">{o.shipping.city}, {o.shipping.county} {o.shipping.postal_code}</p>
                          <p className="text-sm text-gray-500 mt-2">{o.shipping.email} · {o.shipping.phone}</p>
                          {o.shipping.notes && <p className="text-sm mt-2 italic">"{o.shipping.notes}"</p>}
                          {o.email_sent_at && (
                            <p className="text-xs text-gray-500 mt-3 inline-flex items-center gap-1"><Mail size={11} /> Email admin trimis: {new Date(o.email_sent_at).toLocaleString("ro-RO")}</p>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">Nicio comandă pentru filtrul selectat.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------------- PRODUCTS ---------------------- */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [view, setView] = useState({ mode: "list", product: null });
  const [filter, setFilter] = useState("all");
  const [translating, setTranslating] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Sigur ștergi produsul "${p.short_name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Produs șters");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const translateOne = async (p) => {
    setTranslating(p.id);
    try {
      await api.post(`/admin/products/${p.id}/translate`);
      toast.success(`Tradus: ${p.short_name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la traducere");
    } finally { setTranslating(null); }
  };

  const translateAll = async () => {
    setTranslating("all");
    try {
      const { data } = await api.post(`/admin/products/translate-all`);
      toast.success(`Traduse: ${data.translated} · sărite: ${data.skipped}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la traducere");
    } finally { setTranslating(null); }
  };

  if (view.mode !== "list") {
    return (
      <div data-testid="product-form-view">
        <button onClick={() => setView({ mode: "list", product: null })} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gray-500 hover:text-gray-900 mb-6" data-testid="back-to-list">
          <ChevronLeft size={14} /> Înapoi la listă
        </button>
        <h2 className="font-display text-2xl font-black tracking-tight mb-2">
          {view.mode === "edit" ? `Editează: ${view.product?.short_name}` : "Produs nou"}
        </h2>
        <p className="text-sm text-gray-500 mb-8">{view.mode === "edit" ? "Modifică datele și apasă Actualizează produs." : "Completează datele și apasă Creează produs."}</p>
        <ProductForm initial={view.product} onSaved={() => { load(); setView({ mode: "list", product: null }); }} onCancel={() => setView({ mode: "list", product: null })} />
      </div>
    );
  }

  const filtered = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <div data-testid="products-list">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {[
            { v: "all", l: "Toate" },
            { v: "cutite", l: "Cuțite" },
            { v: "scule", l: "Scule" },
          ].map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)} className={`px-4 py-2 text-xs uppercase tracking-wider border ${filter === f.v ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-600 hover:border-gray-900'}`} data-testid={`product-filter-${f.v}`}>
              {f.l}
            </button>
          ))}
        </div>
        <button onClick={() => setView({ mode: "create", product: null })} className="bg-gray-900 text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-800 inline-flex items-center gap-2" data-testid="new-product-btn">
          <Plus size={14} /> Produs nou
        </button>
      </div>

      <div className="mb-4 flex items-center justify-between flex-wrap gap-3 border border-dashed border-gray-300 bg-gray-50/40 px-4 py-3">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Languages size={14} strokeWidth={1.5} className="text-gray-500" />
          <span>Traduceri automate (RO → EN + IT) pentru toate produsele. Rulează o singură dată sau ori de câte ori adaugi/modifici produse.</span>
        </div>
        <button
          onClick={translateAll}
          disabled={translating === "all"}
          className="border border-gray-900 px-4 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white transition disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="translate-all-btn"
        >
          <Languages size={12} /> {translating === "all" ? "Se traduce..." : "Tradu toate produsele"}
        </button>
      </div>

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">Imagine</th>
              <th className="px-4 py-3">Produs</th>
              <th className="px-4 py-3">Categorie</th>
              <th className="px-4 py-3 text-right">Preț</th>
              <th className="px-4 py-3">Tier</th>
              <th className="px-4 py-3 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50" data-testid={`admin-product-${p.id}`}>
                <td className="px-4 py-3 w-16">
                  <div className="w-12 h-12 border border-gray-200 bg-white overflow-hidden">
                    <img src={p.images[0]} alt="" className="w-full h-full object-contain p-1" />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold">{p.short_name}</p>
                  <p className="text-xs text-gray-500 font-mono">#{p.id}</p>
                </td>
                <td className="px-4 py-3 text-xs uppercase tracking-wider">{p.category}</td>
                <td className="px-4 py-3 text-right font-mono">{formatRON(p.price)}</td>
                <td className="px-4 py-3 text-xs">
                  {p.quantity_discount_tiers?.length > 0
                    ? p.quantity_discount_tiers.map((t) => `${t.qty}+→${t.percent}%`).join(" · ")
                    : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${p.translations?.en && p.translations?.it ? 'border-green-500 text-green-700' : 'border-gray-300 text-gray-400'}`} title="Status traduceri EN+IT">
                      {p.translations?.en && p.translations?.it ? "EN·IT ✓" : "RO only"}
                    </span>
                    <button
                      onClick={() => translateOne(p)}
                      disabled={translating === p.id}
                      className="p-2 border border-gray-300 hover:border-gray-900 disabled:opacity-50"
                      title="Traduce în EN + IT"
                      data-testid={`translate-product-${p.id}`}
                    >
                      <Languages size={14} className={translating === p.id ? "animate-pulse" : ""} />
                    </button>
                    <button onClick={() => setView({ mode: "edit", product: p })} className="p-2 border border-gray-300 hover:border-gray-900" title="Editează" data-testid={`edit-product-${p.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 border border-gray-300 hover:border-red-600 hover:bg-red-50 text-gray-700 hover:text-red-600" title="Șterge" data-testid={`delete-product-${p.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Nicio listare în această categorie.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
